/**
 * BellLibraryScreen — 16 synthesized instruments grouped by tradition.
 *
 * Tap to hear · hold (or long-press) to set as default.
 * UI-INV-005: Leaf component, props only, no engine imports.
 */
import React from "react";
import type { BellId } from "../types/ui";
import { BELL_CATALOGUE, BELL_TRADITION_ORDER } from "../data/bellCatalogue";
import { BellIcon, BELL_ICON_SIZE } from "../components/BellIcon";
import { MetaStrip } from "../components/MetaStrip";
import styles from "./BellLibraryScreen.module.css";
import { TEST_IDS } from "../testing/testIds";
import { TIMING } from "../constants";

type Props = {
  defaultBellId: BellId;
  onPreview: (id: BellId) => void;
  onSetDefault: (id: BellId) => void;
  onBack: () => void;
};

const GROUPS = BELL_TRADITION_ORDER.map((tradition) => ({
  tradition,
  bells: BELL_CATALOGUE.filter((b) => b.tradition === tradition),
})).filter((g) => g.bells.length > 0);

export function BellLibraryScreen({
  defaultBellId,
  onPreview,
  onSetDefault,
  onBack,
}: Props) {
  return (
    <div className={styles.screen} data-testid={TEST_IDS.BELL_LIBRARY_SCREEN}>
      <MetaStrip label="Sound" onBack={onBack} />

      <div className={styles.content}>
        <h1 className={styles.heading}>Bell Library</h1>
        <div className={styles.hint}>tap to hear · hold to set default</div>

        <div className={styles.list}>
          {GROUPS.map((group, gi) => (
            <React.Fragment key={group.tradition}>
              <div className={styles.sectionHeading}>{group.tradition}</div>
              {group.bells.map((bell, bi) => {
                const isDefault = bell.id === defaultBellId;
                const isLast =
                  gi === GROUPS.length - 1 && bi === group.bells.length - 1;
                return (
                  <button
                    key={bell.id}
                    className={`${styles.row} ${isLast ? styles.rowLast : ""} ${isDefault ? styles.rowActive : ""}`}
                    onClick={() => onPreview(bell.id)}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      onSetDefault(bell.id);
                    }}
                    onPointerDown={(e) => {
                      if (e.pointerType === "touch") {
                        const timer = setTimeout(
                          () => onSetDefault(bell.id),
                          TIMING.BELL_HOLD_DELAY_MS,
                        );
                        const cancel = () => clearTimeout(timer);
                        e.currentTarget.addEventListener("pointerup", cancel, { once: true });
                        e.currentTarget.addEventListener("pointercancel", cancel, { once: true });
                      }
                    }}
                    type="button"
                    aria-label={`${bell.name} — ${group.tradition}${isDefault ? " (default)" : ""}`}
                    data-bell-id={bell.id}
                    data-testid={TEST_IDS.TEACHING_BELL_CARD}
                  >
                    <BellIcon id={bell.id} size={BELL_ICON_SIZE.ROW} className={styles.rowIcon} />
                    <div className={styles.rowMain}>
                      <div className={styles.rowName}>{bell.name}</div>
                      <div className={styles.rowMeta}>{bell.traditionalName}</div>
                    </div>
                    <div className={styles.rowRight}>
                      <div className={styles.rowDecay}>{bell.decayLabel}</div>
                      {isDefault && <div className={styles.defaultDot} />}
                    </div>
                  </button>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}
